/**
 * Atendimento frustrado no modal de confirmação de realização (SweetAlert).
 *
 * O serviço foi executado — o profissional se deslocou — mas o atendimento não pôde
 * acontecer. Nesses casos o programa cobra apenas um percentual do valor, não há arquivo
 * de monitoramento para anexar e a justificativa passa a ser obrigatória.
 *
 * As três telas que confirmam realização (AtendimentosEdit, Agendas e Agendamentos) usam o
 * mesmo HTML montado à mão dentro do Swal, então o bloco vive aqui em vez de ser copiado.
 */

const ID_CHECKBOX = 'swal-check-frustrado';
const ID_OBSERVACAO = 'swal-textarea-observacao-frustrado';
const ID_BLOCO_OBSERVACAO = 'swal-bloco-observacao-frustrado';
const ID_BLOCO_MONITORIA = 'swal-bloco-monitoria';
const ID_BLOCO_LAUDO = 'swal-bloco-laudo';

/**
 * Indica se o serviço do agendamento libera a marcação de atendimento frustrado.
 */
export function permiteAtendimentoFrustrado(agendamento) {
    return agendamento?.dados_servico_procedimento?.permite_atendimento_frustrado === true;
}

/**
 * HTML do bloco (checkbox + observação). Devolve string vazia quando o serviço não permite,
 * para poder ser interpolado direto no html do Swal.
 */
export function montarHtmlAtendimentoFrustrado(agendamento) {
    if (!permiteAtendimentoFrustrado(agendamento)) return '';

    const percentual = agendamento?.dados_servico_procedimento?.percentual_cobranca_frustrado;
    const avisoPercentual = (percentual !== null && percentual !== undefined)
        ? ` Será cobrado ${String(percentual).replace('.', ',')}% do valor do serviço.`
        : '';

    return `
        <div class="mt-3 pt-3 border-top">
            <div class="form-check text-start">
                <input class="form-check-input" type="checkbox" id="${ID_CHECKBOX}" />
                <label class="form-check-label" for="${ID_CHECKBOX}">Atendimento frustrado</label>
            </div>
            <div class="text-start small text-muted ms-4">
                Marque quando o serviço foi executado mas o atendimento não pôde ser realizado.${avisoPercentual}
            </div>
            <div id="${ID_BLOCO_OBSERVACAO}" class="mt-2" style="display: none;">
                <label for="${ID_OBSERVACAO}" class="text-start form-label">
                    Observação do atendimento frustrado <span class="text-danger">*</span>
                </label>
                <textarea id="${ID_OBSERVACAO}" class="form-control"
                    placeholder="Descreva o que impediu a realização do atendimento" rows="3"></textarea>
            </div>
        </div>
    `;
}

/**
 * Campos dinâmicos que continuam valendo com o atendimento frustrado. Os de arquivo saem:
 * não houve atendimento, então não há evidência a anexar — e obrigatórios travariam o envio.
 */
export function camposDinamicosAtivos(campos, frustrado) {
    if (!frustrado) return campos || [];

    return (campos || []).filter(campo => campo.tipo !== 'file');
}

/**
 * Liga o checkbox aos campos que dependem dele: mostra a observação e esconde os blocos de
 * arquivo (monitoramento e campos dinâmicos do tipo arquivo), que deixam de ser exigidos.
 *
 * `onToggle` recebe o novo estado — as telas usam para liberar o botão de confirmar, que a
 * validação do PDF de monitoramento pode ter deixado desabilitado.
 */
export function configurarEventosAtendimentoFrustrado(agendamento, onToggle, camposDinamicos) {
    if (!permiteAtendimentoFrustrado(agendamento)) return;

    const checkbox = document.getElementById(ID_CHECKBOX);

    if (!checkbox) return;

    const camposArquivo = (camposDinamicos || []).filter(campo => campo.tipo === 'file');

    checkbox.addEventListener('change', () => {
        const frustrado = checkbox.checked;
        const blocoObservacao = document.getElementById(ID_BLOCO_OBSERVACAO);
        const blocoMonitoria = document.getElementById(ID_BLOCO_MONITORIA);

        if (blocoObservacao) {
            blocoObservacao.style.display = frustrado ? 'block' : 'none';
        }

        if (blocoMonitoria) {
            blocoMonitoria.style.display = frustrado ? 'none' : 'block';

            //descarta arquivo escolhido antes da marcação, para não subir junto
            if (frustrado) {
                const inputArquivo = document.getElementById('swal-input-arquivo-monitoria');
                if (inputArquivo) inputArquivo.value = '';
            }
        }

        const blocoLaudo = document.getElementById(ID_BLOCO_LAUDO);

        if (blocoLaudo) {
            blocoLaudo.style.display = frustrado ? 'none' : 'block';

            if (frustrado) {
                const inputLaudo = document.getElementById('swal-input-arquivo');
                if (inputLaudo) inputLaudo.value = '';
            }
        }

        camposArquivo.forEach(campo => {
            const bloco = document.querySelector(`.campo-dinamico[data-slug="${campo.slug}"]`);
            if (bloco) bloco.style.display = frustrado ? 'none' : 'block';

            if (frustrado) {
                const input = document.getElementById(`swal-campo-${campo.slug}`);
                if (input) input.value = '';
            }
        });

        if (typeof onToggle === 'function') onToggle(frustrado);
    });
}

/**
 * Lê o bloco no preConfirm. Devolve `null` quando a observação obrigatória está vazia, para a
 * tela mostrar a mensagem de validação e abortar o envio.
 */
export function lerAtendimentoFrustrado(agendamento) {
    if (!permiteAtendimentoFrustrado(agendamento)) {
        return { frustrado: false, observacao: '' };
    }

    const frustrado = document.getElementById(ID_CHECKBOX)?.checked === true;

    if (!frustrado) {
        return { frustrado: false, observacao: '' };
    }

    const observacao = (document.getElementById(ID_OBSERVACAO)?.value || '').trim();

    if (!observacao) return null;

    return { frustrado: true, observacao };
}

export const MSG_OBSERVACAO_FRUSTRADO_OBRIGATORIA = 'Informe a observação sobre o atendimento frustrado';
